import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';

/**
 * FeatureGateProvider — fetches the user's plan, gated features, plan limits,
 * monthly usage and admin-editable upgrade messages from a single endpoint
 * (/api/my/feature-access) and exposes them via React context.
 *
 * Extended Feb 14, 2026 — backend now returns `limits`, `usage` and
 * `upgrade_messages` alongside `gated_features`, so admin edits in
 * Admin → Offers propagate to every lock-state in the UI with no redeploy.
 */
const FeatureGateCtx = createContext({
  gatedFeatures: {},
  limits: {},
  usage: {},
  upgradeMessages: {},
  planName: '',
  planId: null,
  category: null,
  isPaidTier: false,
  badgeTier: null,
  loaded: false,
});

export function FeatureGateProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    gatedFeatures: {}, limits: {}, usage: {}, upgradeMessages: {},
    planName: '', planId: null, category: null,
    isPaidTier: false, badgeTier: null, loaded: false,
  });

  useEffect(() => {
    if (!user) {
      setState(s => ({ ...s, planName: 'Free', loaded: true }));
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API}/my/feature-access`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setState({
          gatedFeatures: res.data.gated_features || {},
          limits: res.data.limits || {},
          usage: res.data.usage || {},
          upgradeMessages: res.data.upgrade_messages || {},
          planName: res.data.plan_name || 'Free',
          planId: res.data.plan_id,
          category: res.data.category,
          isPaidTier: !!res.data.is_paid_tier,
          badgeTier: res.data.badge_tier,
          loaded: true,
        });
      })
      .catch(() => setState((s) => ({ ...s, loaded: true })));
  }, [user]);

  return <FeatureGateCtx.Provider value={state}>{children}</FeatureGateCtx.Provider>;
}

export function useFeatureGate() {
  const ctx = useContext(FeatureGateCtx);

  /** Returns true if the user's plan has `featureKey` enabled. */
  const isUnlocked = (featureKey) => {
    if (!featureKey) return true;
    // Until the plan loads, be conservative for role-scoped admin features
    // (matches the leak-fix from useFeatureFlags) — return false so we
    // never flash locked content visible. Once loaded, read the real value.
    if (!ctx.loaded) return false;
    if (!ctx.gatedFeatures || Object.keys(ctx.gatedFeatures).length === 0) return true;
    return ctx.gatedFeatures[featureKey] === true;
  };

  /** Look up an admin-editable upgrade message by role + key. */
  const getUpgradeMessage = (key, fallback = '') => {
    if (!key) return fallback;
    const role = ctx.category || 'student';
    const roleKey =
      role === 'agent' || role === 'visa_agent'
        ? 'agent'
        : role === 'institution' || role === 'rpl_provider'
        ? 'institution'
        : role;
    const msgs = (ctx.upgradeMessages || {})[roleKey] || {};
    return msgs[key] || fallback;
  };

  /** Returns the configured limit for a counter (null === unlimited). */
  const getLimit = (counterKey) => {
    const v = (ctx.limits || {})[counterKey];
    return v === undefined ? null : v;
  };

  /** Returns current usage for a counter (defaults 0). */
  const getUsage = (counterKey) => Number((ctx.usage || {})[counterKey] || 0);

  /** True if user has remaining quota or limit is null (unlimited). */
  const hasQuotaLeft = (counterKey) => {
    const lim = getLimit(counterKey);
    if (lim === null || lim === undefined) return true;
    return getUsage(counterKey) < Number(lim);
  };

  return {
    ...ctx,
    isUnlocked,
    getUpgradeMessage,
    getLimit,
    getUsage,
    hasQuotaLeft,
  };
}
